import React, { ChangeEvent, useEffect, useMemo, useRef, useState } from 'react';
import './App.css';

type ArtistPayload = {
  name: string;
  bio: string;
  country: string;
  is_band: 0 | 1;
};

type ArtistUpdatePayload = ArtistPayload & { artistID: string };

type ArtistImagePayload = {
  artistID: string;
  file: string;
  fileName: string;
  extension: string;
};

type AlbumPayload = {
  title: string;
  description: string;
  release_date?: string;
  isPublic: 0 | 1;
  artistID: string;
};

type AlbumImagePayload = {
  albumID: string;
  file: string;
  fileName: string;
  extension: string;
};

type TrackItem = {
  title: string;
  file: string;
  fileName: string;
  extension: string;
  duration: number;
  trackNumber: number;
  explicit: 0 | 1;
};

type TrackPayload = {
  artistID: string;
  albumID: string;
  tracks: TrackItem[];
};

type PlaylistPayload = {
  title: string;
  description: string;
  isPublic: 0 | 1;
};

type AddTrackToPlaylistPayload = {
  playlistID: string;
  trackID: string;
};

type RequestState = 'idle' | 'loading' | 'success' | 'error';

type ResponseState = {
  status: RequestState;
  message: string;
};

type TrackUploadItem = {
  id: string;
  label: string;
  estimatedBytes: number;
  progress: number;
};

type TrackUploadState = {
  status: 'idle' | 'uploading' | 'error';
  overallPercent: number;
  currentItemId?: string;
  items: TrackUploadItem[];
};

const emptyArtist: ArtistPayload = {
  name: 'Static Pulse',
  bio: 'Modern nu-metal ve elektronik fusion projesi. Dijital kaos, analog duygu.',
  country: 'Türkiye',
  is_band: 1,
};

const emptyAlbum: AlbumPayload = {
  title: 'A Piece of Anatolia',
  description: "Nightforge'in ilk albümü.",
  release_date: '2025-12-01',
  isPublic: 1,
  artistID: '123213',
};

const emptyTrack: TrackItem = {
  title: '',
  file: '',
  fileName: '',
  extension: 'wav',
  duration: 0,
  trackNumber: 1,
  explicit: 0,
};

function App() {
  const [token, setToken] = useState<string>('');
  const [responses, setResponses] = useState<Record<string, ResponseState>>({});
  const [artistPayload, setArtistPayload] = useState<ArtistPayload>(emptyArtist);
  const [artistUpdate, setArtistUpdate] = useState<ArtistUpdatePayload>({
    artistID: 'ARTIST_KODU_BURAYA',
    ...emptyArtist,
  });
  const [artistImage, setArtistImage] = useState<ArtistImagePayload>({
    artistID: 'ARTIST_KODU_BURAYA',
    file: '',
    fileName: 'static-pulse-profile.png',
    extension: 'png',
  });
  const [albumPayload, setAlbumPayload] = useState<AlbumPayload>(emptyAlbum);
  const [albumImage, setAlbumImage] = useState<AlbumImagePayload>({
    albumID: 'ALBUM_KODU_BURAYA',
    file: '',
    fileName: 'nightforge-piece-of-anatolia.png',
    extension: 'png',
  });
  const [trackAlbumID, setTrackAlbumID] = useState<string>('ALBUM_KODU');
  const [trackArtistID, setTrackArtistID] = useState<string>('123213');
  const [trackDraft, setTrackDraft] = useState<TrackItem>(emptyTrack);
  const [tracks, setTracks] = useState<TrackItem[]>([]);
  const [trackUpload, setTrackUpload] = useState<TrackUploadState>({
    status: 'idle',
    overallPercent: 0,
    items: [],
  });
  const [playlistPayload, setPlaylistPayload] = useState<PlaylistPayload>({
    title: 'Night Coding Session',
    description: 'Gece kod yazarken dinlemek için.',
    isPublic: 1,
  });
  const [playlistTrack, setPlaylistTrack] = useState<AddTrackToPlaylistPayload>({
    playlistID: 'playlistID',
    trackID: 'trackID',
  });

  const trackPayload = useMemo<TrackPayload>(
    () => ({
      artistID: trackArtistID,
      albumID: trackAlbumID,
      tracks,
    }),
    [trackArtistID, trackAlbumID, tracks],
  );

  const trackPayloadDisplay = useMemo<TrackPayload>(
    () => ({
      ...trackPayload,
      tracks: trackPayload.tracks.map(t => ({ ...t, file: '<base64 omitted>' })),
    }),
    [trackPayload],
  );

  useEffect(() => {
    const saved = localStorage.getItem('nova-token');
    if (saved) setToken(saved);
  }, []);

  const setResponse = (key: string, state: ResponseState) =>
    setResponses(prev => ({ ...prev, [key]: state }));

  const trackFileInputRef = useRef<HTMLInputElement | null>(null);

  const estimateBase64Bytes = (dataUrl: string) => {
    const base64 = dataUrl.split(',')[1] || '';
    if (!base64) return 0;
    const padding = base64.endsWith('==') ? 2 : base64.endsWith('=') ? 1 : 0;
    return Math.max(0, Math.floor((base64.length * 3) / 4) - padding);
  };

  const getNested = (value: unknown, path: string[]) => {
    let current: any = value;
    for (const key of path) {
      if (!current || typeof current !== 'object') return undefined;
      current = current[key];
    }
    return current;
  };

  const extractFirstString = (value: unknown, candidatePaths: string[][]) => {
    for (const path of candidatePaths) {
      const found = getNested(value, path);
      if (typeof found === 'string' && found.trim()) return found.trim();
      if (typeof found === 'number' && Number.isFinite(found)) return String(found);
    }
    return '';
  };

  const applyArtistIDEverywhere = (artistID: string) => {
    setArtistUpdate(prev => ({ ...prev, artistID }));
    setArtistImage(prev => ({ ...prev, artistID }));
    setAlbumPayload(prev => ({ ...prev, artistID }));
    setTrackArtistID(artistID);
  };

  const applyAlbumIDEverywhere = (albumID: string) => {
    setAlbumImage(prev => ({ ...prev, albumID }));
    setTrackAlbumID(albumID);
  };

  const getToken = async () => {
    setResponse('token', { status: 'loading', message: 'Token alınıyor...' });
    try {
      const res = await fetch('https://prod.apiollon.com/get-token', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          key: 'F0RRDrlPigAYTmOSweyhBmo6aYZOLpTA8qGCGQe2wkmDHJYBF9',
          secret: 'anHHiSHfgOZ5zJKxWGJPO2PPNdIgL2HwdgUchEuC7bh5BLU',
        }),
      });
      const data = await res.json();
      if (!data.status) {
        throw new Error(data?.message || 'Token alınamadı');
      }
      const bearer =data?.result.token;
      if (!bearer) {
        throw new Error('Token boş döndü, API yanıtını kontrol et');
      }
      setToken(bearer);
      localStorage.setItem('nova-token', bearer);
      setResponse('token', { status: 'success', message: bearer });
    } catch (error: unknown) {
      setResponse('token', {
        status: 'error',
        message: error instanceof Error ? error.message : 'Token alınamadı',
      });
    }
  };

  type AuthedPostOptions = {
    onSuccess?: (data: any) => void;
    successMessage?: string | ((data: any) => string);
  };

  const authedPost = async (
    key: string,
    url: string,
    payload: unknown,
    options?: AuthedPostOptions,
  ) => {
    const bearer = token || localStorage.getItem('nova-token') || '';
    if (!bearer) {
      setResponse(key, { status: 'error', message: 'Önce token almalısın.' });
      return;
    }
    setResponse(key, { status: 'loading', message: 'İstek gönderiliyor...' });
    try {
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${bearer}`,
        },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data?.message || 'İstek başarısız');
      }

      const message =
        typeof options?.successMessage === 'function'
          ? options.successMessage(data)
          : options?.successMessage ?? JSON.stringify(data, null, 2);
      setResponse(key, {
        status: 'success',
        message,
      });
      options?.onSuccess?.(data);
    } catch (error: unknown) {
      setResponse(key, {
        status: 'error',
        message: error instanceof Error ? error.message : 'Bilinmeyen hata',
      });
    }
  };

  const authedPostXhr = (
    url: string,
    payload: unknown,
    onProgress?: (loaded: number, total: number) => void,
  ) => {
    const bearer = token || localStorage.getItem('nova-token') || '';
    if (!bearer) {
      return Promise.reject(new Error('Önce token almalısın.'));
    }

    return new Promise<any>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', url, true);
      xhr.setRequestHeader('Content-Type', 'application/json');
      xhr.setRequestHeader('Authorization', `Bearer ${bearer}`);

      xhr.upload.onprogress = event => {
        if (!onProgress) return;
        if (event.lengthComputable) {
          onProgress(event.loaded, event.total);
        } else {
          onProgress(event.loaded, 0);
        }
      };

      xhr.onload = () => {
        const isOk = xhr.status >= 200 && xhr.status < 300;
        let parsed: any = undefined;
        try {
          parsed = xhr.responseText ? JSON.parse(xhr.responseText) : undefined;
        } catch {
          parsed = xhr.responseText;
        }
        if (!isOk) {
          reject(new Error(parsed?.message || `İstek başarısız (${xhr.status})`));
          return;
        }
        resolve(parsed);
      };

      xhr.onerror = () => reject(new Error('Ağ hatası'));
      xhr.send(JSON.stringify(payload));
    });
  };

  const uploadTracksBundle = async () => {
    if (tracks.length === 0) {
      setResponse('tracks-create', { status: 'error', message: 'Önce track eklemelisin.' });
      return;
    }
    if (!trackArtistID.trim() || !trackAlbumID.trim()) {
      setResponse('tracks-create', { status: 'error', message: 'Artist ID ve Album ID zorunlu.' });
      return;
    }

    const payloadSnapshot: TrackPayload = {
      artistID: trackArtistID,
      albumID: trackAlbumID,
      tracks: tracks.map(t => ({ ...t })),
    };

    const invalidTrack = payloadSnapshot.tracks.find(t => !t.file || !t.fileName);
    if (invalidTrack) {
      setResponse('tracks-create', { status: 'error', message: 'Track dosyası eksik görünüyor.' });
      return;
    }

    const items = payloadSnapshot.tracks.map((t, index) => {
      const label = t.title || t.fileName || `Track ${t.trackNumber}`;
      return {
        id: `${index}-${t.trackNumber}-${t.fileName || label}`,
        label,
        estimatedBytes: Math.max(1, estimateBase64Bytes(t.file)),
        progress: 0,
      };
    });

    const totalEstimatedBytes = items.reduce((sum, item) => sum + item.estimatedBytes, 0);

    setTrackUpload({
      status: 'uploading',
      overallPercent: 0,
      currentItemId: items[0]?.id,
      items,
    });
    setResponse('tracks-create', { status: 'loading', message: 'Yükleniyor...' });

    try {
      await authedPostXhr(
        'https://prod.apiollon.com/flow/nova-create-tracks',
        payloadSnapshot,
        (loaded, total) => {
          const overallPercent =
            total > 0 ? Math.min(100, Math.max(0, (loaded / total) * 100)) : 0;

          const loadedEstimatedBytes =
            totalEstimatedBytes > 0 && total > 0
              ? (loaded / total) * totalEstimatedBytes
              : 0;

          let remaining = loadedEstimatedBytes;
          const nextItems = items.map(item => {
            const portion = item.estimatedBytes > 0 ? Math.min(item.estimatedBytes, remaining) : 0;
            remaining = Math.max(0, remaining - portion);
            const progress =
              item.estimatedBytes > 0 ? Math.min(100, Math.max(0, (portion / item.estimatedBytes) * 100)) : 0;
            return { ...item, progress };
          });

          const currentItemId =
            nextItems.find(i => i.progress < 100)?.id ?? nextItems[nextItems.length - 1]?.id;

          setTrackUpload(prev => ({
            ...prev,
            overallPercent,
            currentItemId,
            items: nextItems,
          }));
        },
      );

      setResponse('tracks-create', { status: 'success', message: 'Yüklendi' });

      setTracks([]);
      setTrackDraft(emptyTrack);
      setResponse('tracks-add', { status: 'idle', message: '' });
      setTrackUpload({ status: 'idle', overallPercent: 0, items: [] });
      if (trackFileInputRef.current) trackFileInputRef.current.value = '';
    } catch (error: unknown) {
      setTrackUpload(prev => ({ ...prev, status: 'error' }));
      setResponse('tracks-create', {
        status: 'error',
        message: error instanceof Error ? error.message : 'Bilinmeyen hata',
      });
    }
  };

  const handleTextChange = <T,>(
    updater: React.Dispatch<React.SetStateAction<T>>,
    key: keyof T,
  ) => (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const value = event.target.value;
    updater(prev => ({ ...prev, [key]: value }));
  };

  const handleToggle = <T,>(
    updater: React.Dispatch<React.SetStateAction<T>>,
    key: keyof T,
  ) => (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.checked ? 1 : 0;
    updater(prev => ({ ...prev, [key]: value }));
  };

  const fileToBase64 = (file: File, keepPrefix: boolean) =>
    new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        if (!result) {
          reject(new Error('Dosya okunamadı'));
          return;
        }
        resolve(keepPrefix ? result : result.split(',')[1] || '');
      };
      reader.onerror = () => reject(new Error('Dosya okunamadı'));
      reader.readAsDataURL(file);
    });

  const handleFileSelect = async (
    event: ChangeEvent<HTMLInputElement>,
    onLoad: (base64: string, file: File) => void,
    keepPrefix = false,
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const base64 = await fileToBase64(file, keepPrefix);
    onLoad(base64, file);
  };

  const getAudioDuration = (file: File): Promise<number> =>
    new Promise((resolve, reject) => {
      const url = URL.createObjectURL(file);
      const audio = new Audio();
      audio.src = url;
      const cleanup = () => URL.revokeObjectURL(url);
      audio.addEventListener('loadedmetadata', () => {
        if (!isFinite(audio.duration)) {
          audio.currentTime = Number.MAX_SAFE_INTEGER;
          audio.ontimeupdate = () => {
            cleanup();
            resolve(Math.round(audio.duration));
          };
        } else {
          cleanup();
          resolve(Math.round(audio.duration));
        }
      });
      audio.onerror = () => {
        cleanup();
        reject(new Error('Süre okunamadı'));
      };
      audio.load();
    });

  const addTrackFromFile = async (base64: string, file: File) => {
    const extension = (file.name.split('.').pop() || '').toLowerCase();
    if (extension !== 'wav') {
      setResponse('tracks-add', {
        status: 'error',
        message: 'Sadece WAV dosyaları yüklenebilir.',
      });
      return;
    }

    const duration = await getAudioDuration(file).catch(() => 0);
    const nextTrackNumber =
      tracks.length > 0 ? Math.max(...tracks.map(t => t.trackNumber)) + 1 : trackDraft.trackNumber;
    const baseName = file.name.replace(/\.[^/.]+$/, '');
    const title = trackDraft.title ? trackDraft.title : baseName;
    const track: TrackItem = {
      ...trackDraft,
      title,
      file: base64,
      fileName: file.name,
      extension,
      trackNumber: nextTrackNumber,
      duration,
    };
    setTracks(prev => [...prev, track]);
    setTrackDraft(prev => ({
      ...prev,
      trackNumber: nextTrackNumber + 1,
      title: '',
      file: '',
      fileName: '',
    }));
    setResponse('tracks-add', {
      status: 'success',
      message: `${file.name} eklendi (${duration || 'bilinmiyor'} sn)`,
    });
  };

  const addTrack = () => {
    setTracks(prev => {
      const next = prev.filter(t => t.trackNumber !== trackDraft.trackNumber);
      const title = trackDraft.title
        ? trackDraft.title
        : trackDraft.fileName
        ? trackDraft.fileName.replace(/\.[^/.]+$/, '')
        : `Track ${trackDraft.trackNumber}`;
      return [...next, { ...trackDraft, title }];
    });
    setTrackDraft(prev => ({
      ...prev,
      title: '',
      file: '',
      fileName: '',
      trackNumber: prev.trackNumber + 1,
    }));
  };

  const renderJSON = (payload: unknown) => (
    <pre className="code-block">{JSON.stringify(payload, null, 2)}</pre>
  );

  const tokenInfo = token ? `${token.slice(0, 8)}...${token.slice(-6)}` : 'Yok';

  return (
    <div className="app-shell">
      <header className="hero">
        <div className="hero__eyebrow">Nova Management · Beta</div>
        <h1>Lodos-Nova Control Room</h1>
        <p>
          Create artists, albums, tracks, playlists, and upload artwork/audio. Build requests in
          seconds and paste them straight into your API calls.
        </p>
      <div className="hero__tags">
        <span>Albums</span>
        <span>Tracks</span>
        <span>Playlists</span>
        <span>Uploads</span>
        <span>Bearer auth</span>
      </div>
    </header>

      <section className="panel token-panel">
        <div className="panel__title">
          <h2>API Token</h2>
          <span className="pill">required</span>
        </div>
        <p className="muted">
          Tüm endpointler için önce token almalısın. Token, Bearer olarak otomatik eklenir.
        </p>
        <div className="form two-col">
          <TextField label="Key" value="PvCD5HzBzqJYfd0iHIpSLuoWESoQqb" onChange={() => {}} />
          <TextField label="Secret" value="ntscjBz7H4S2rnmIyjWy3nnSyPhi13" onChange={() => {}} />
          <TextField
            label="Token (manuel yapıştır)"
            value={token}
            onChange={e => {
              const value = e.target.value;
              setToken(value);
              localStorage.setItem('nova-token', value);
            }}
            placeholder="API'den dönen Bearer değerini yapıştır"
          />
        </div>
        <div className="actions">
          <button className="action" type="button" onClick={getToken}>
            Token al
          </button>
          <div className="token-chip">{token ? `Bearer: ${tokenInfo}` : 'Token yok'}</div>
        </div>
        <ResponseBox state={responses['token']} />
      </section>

    <div className="grid">
        <section className="panel">
          <div className="panel__title">
            <h2>Artist</h2>
            <span className="pill">create</span>
          </div>
          <div className="form">
            <TextField
              label="Name"
              value={artistPayload.name}
              onChange={handleTextChange(setArtistPayload, 'name')}
            />
            <TextArea
              label="Bio"
              value={artistPayload.bio}
              onChange={handleTextChange(setArtistPayload, 'bio')}
            />
            <TextField
              label="Country"
              value={artistPayload.country}
              onChange={handleTextChange(setArtistPayload, 'country')}
            />
            <Toggle
              label="Is band?"
              checked={artistPayload.is_band === 1}
              onChange={handleToggle(setArtistPayload, 'is_band')}
            />
          </div>
          <button
            className="action"
            type="button"
            onClick={() =>
              authedPost('artist-create', 'https://prod.apiollon.com/flow/nova-create-artist', artistPayload, {
                onSuccess: data => {
                  const artistID = extractFirstString(data, [
                    ['result', 'artistID'],
                    ['result', 'artistId'],
                    ['result', 'artist_id'],
                    ['artistID'],
                    ['artistId'],
                    ['artist_id'],
                    ['result', 'id'],
                    ['id'],
                  ]);
                  if (artistID) applyArtistIDEverywhere(artistID);
                },
              })
            }
          >
            Create artist
          </button>
          {renderJSON(artistPayload)}
          <ResponseBox state={responses['artist-create']} />
        </section>

        <section className="panel">
          <div className="panel__title">
            <h2>Artist</h2>
            <span className="pill pill--outline">update</span>
          </div>
          <div className="form">
            <TextField
              label="Artist ID"
              value={artistUpdate.artistID}
              onChange={handleTextChange(setArtistUpdate, 'artistID')}
            />
            <TextField
              label="Name"
              value={artistUpdate.name}
              onChange={handleTextChange(setArtistUpdate, 'name')}
            />
            <TextArea
              label="Bio"
              value={artistUpdate.bio}
              onChange={handleTextChange(setArtistUpdate, 'bio')}
            />
            <TextField
              label="Country"
              value={artistUpdate.country}
              onChange={handleTextChange(setArtistUpdate, 'country')}
            />
            <Toggle
              label="Is band?"
              checked={artistUpdate.is_band === 1}
              onChange={handleToggle(setArtistUpdate, 'is_band')}
            />
          </div>
          <button
            className="action"
            type="button"
            onClick={() =>
              authedPost('artist-update', 'https://prod.apiollon.com/flow/nova-update-artist', artistUpdate)
            }
          >
            Update artist
          </button>
          {renderJSON(artistUpdate)}
          <ResponseBox state={responses['artist-update']} />
        </section>

        <section className="panel">
          <div className="panel__title">
            <h2>Artist Image</h2>
            <span className="pill">upload</span>
          </div>
          <div className="form">
            <TextField
              label="Artist ID"
              value={artistImage.artistID}
              onChange={handleTextChange(setArtistImage, 'artistID')}
            />
            <TextField
              label="File name"
              value={artistImage.fileName}
              onChange={handleTextChange(setArtistImage, 'fileName')}
            />
            <div className="file-row">
              <label>Artist image</label>
              <input
                type="file"
                accept="image/*"
                onChange={e =>
                  handleFileSelect(
                    e,
                    (base64, file) =>
                      setArtistImage(prev => ({
                        ...prev,
                        file: base64,
                        fileName: file.name,
                        extension: file.name.split('.').pop() || 'png',
                      })),
                    true,
                  )
                }
              />
            </div>
          </div>
          <button
            className="action"
            type="button"
            onClick={() =>
              authedPost(
                'artist-image',
                'https://prod.apiollon.com/flow/nova-upload-artist-image',
                artistImage,
              )
            }
          >
            Upload artist image
          </button>
          {renderJSON(artistImage)}
          <ResponseBox state={responses['artist-image']} />
        </section>

        <section className="panel">
          <div className="panel__title">
            <h2>Album</h2>
            <span className="pill">create</span>
          </div>
          <div className="form">
            <TextField
              label="Title"
              value={albumPayload.title}
              onChange={handleTextChange(setAlbumPayload, 'title')}
            />
            <TextArea
              label="Description"
              value={albumPayload.description}
              onChange={handleTextChange(setAlbumPayload, 'description')}
            />
            <TextField
              label="Release date"
              value={albumPayload.release_date ?? ''}
              onChange={handleTextChange(setAlbumPayload, 'release_date')}
              placeholder="YYYY-MM-DD (optional)"
            />
            <TextField
              label="Artist ID"
              value={albumPayload.artistID}
              onChange={handleTextChange(setAlbumPayload, 'artistID')}
            />
            <Toggle
              label="Public?"
              checked={albumPayload.isPublic === 1}
              onChange={handleToggle(setAlbumPayload, 'isPublic')}
            />
          </div>
          <button
            className="action"
            type="button"
            onClick={() =>
              authedPost('album-create', 'https://prod.apiollon.com/flow/nova-create-album', albumPayload, {
                onSuccess: data => {
                  const albumID = extractFirstString(data, [
                    ['result', 'albumID'],
                    ['result', 'albumId'],
                    ['result', 'album_id'],
                    ['albumID'],
                    ['albumId'],
                    ['album_id'],
                    ['result', 'id'],
                    ['id'],
                  ]);
                  if (albumID) applyAlbumIDEverywhere(albumID);
                },
              })
            }
          >
            Create album
          </button>
          {renderJSON(albumPayload)}
          <ResponseBox state={responses['album-create']} />
        </section>

        <section className="panel">
          <div className="panel__title">
            <h2>Album Image</h2>
            <span className="pill">upload</span>
          </div>
          <div className="form">
            <TextField
              label="Album ID"
              value={albumImage.albumID}
              onChange={handleTextChange(setAlbumImage, 'albumID')}
            />
            <TextField
              label="File name"
              value={albumImage.fileName}
              onChange={handleTextChange(setAlbumImage, 'fileName')}
            />
            <div className="file-row">
              <label>Album cover</label>
              <input
                type="file"
                accept="image/*"
                onChange={e =>
                  handleFileSelect(
                    e,
                    (base64, file) =>
                      setAlbumImage(prev => ({
                        ...prev,
                        file: base64,
                        fileName: file.name,
                        extension: file.name.split('.').pop() || 'png',
                      })),
                  )
                }
              />
            </div>
          </div>
          <button
            className="action"
            type="button"
            onClick={() =>
              authedPost(
                'album-image',
                'https://prod.apiollon.com/flow/nova-create-album-cover',
                albumImage,
              )
            }
          >
            Upload album cover
          </button>
          {renderJSON(albumImage)}
          <ResponseBox state={responses['album-image']} />
        </section>

        <section className="panel">
          <div className="panel__title">
            <h2>Tracks</h2>
            <span className="pill pill--outline">bundle</span>
          </div>
          <div className="form two-col">
            <TextField
              label="Album ID"
              value={trackAlbumID}
              onChange={e => setTrackAlbumID(e.target.value)}
            />
            <TextField
              label="Artist ID"
              value={trackArtistID}
              onChange={e => setTrackArtistID(e.target.value)}
            />
            <TextField
              label="Title"
              value={trackDraft.title}
              onChange={handleTextChange(setTrackDraft, 'title')}
            />
            <TextField
              label="Track number"
              type="number"
              value={trackDraft.trackNumber}
              onChange={e =>
                setTrackDraft(prev => ({ ...prev, trackNumber: Number(e.target.value) }))
              }
            />
            <Toggle
              label="Explicit?"
              checked={trackDraft.explicit === 1}
              onChange={handleToggle(setTrackDraft, 'explicit')}
            />
            <div className="file-row">
              <label>Audio file</label>
              <input
                type="file"
                multiple
                accept="audio/wav,.wav"
                onChange={async e => {
                  const files = Array.from(e.target.files || []);
                  if (files.length === 0) return;

                  for (const file of files) {
                    const base64 = await fileToBase64(file, true);
                    await addTrackFromFile(base64, file);
                  }
                  e.target.value = '';
                }}
                ref={trackFileInputRef}
              />
            </div>
            <button className="action" type="button" onClick={addTrack}>
              Add track to bundle
            </button>
          </div>
          {trackUpload.status === 'uploading' && trackUpload.items.length > 0 ? (
            <div className="upload-status">
              <div className="upload-status__headline">
                <strong>Yükleniyor:</strong>{' '}
                {trackUpload.items.find(i => i.id === trackUpload.currentItemId)?.label ??
                  trackUpload.items[0].label}
                <span className="upload-status__percent">
                  {Math.round(trackUpload.overallPercent)}%
                </span>
              </div>
              <div className="upload-status__bar" aria-hidden="true">
                <div style={{ width: `${trackUpload.overallPercent}%` }} />
              </div>
              <div className="upload-list">
                {trackUpload.items.map(item => (
                  <div
                    key={item.id}
                    className={
                      item.id === trackUpload.currentItemId
                        ? 'upload-list__item upload-list__item--active'
                        : 'upload-list__item'
                    }
                  >
                    <span className="upload-list__name">{item.label}</span>
                    <span className="upload-list__percent">{Math.round(item.progress)}%</span>
                  </div>
                ))}
              </div>
            </div>
          ) : null}
          <button
            className="action"
            type="button"
            onClick={uploadTracksBundle}
            disabled={trackUpload.status === 'uploading'}
          >
            Send tracks bundle
          </button>
          {renderJSON(trackPayloadDisplay)}
          <ResponseBox state={responses['tracks-add']} />
          <ResponseBox state={responses['tracks-create']} />
        </section>

        <section className="panel">
          <div className="panel__title">
            <h2>Playlist</h2>
            <span className="pill">create</span>
          </div>
          <div className="form">
            <TextField
              label="Title"
              value={playlistPayload.title}
              onChange={handleTextChange(setPlaylistPayload, 'title')}
            />
            <TextArea
              label="Description"
              value={playlistPayload.description}
              onChange={handleTextChange(setPlaylistPayload, 'description')}
            />
            <Toggle
              label="Public?"
              checked={playlistPayload.isPublic === 1}
              onChange={handleToggle(setPlaylistPayload, 'isPublic')}
            />
          </div>
          <button
            className="action"
            type="button"
            onClick={() =>
              authedPost(
                'playlist-create',
                'https://prod.apiollon.com/flow/nova-create-playlist',
                playlistPayload,
              )
            }
          >
            Create playlist
          </button>
          {renderJSON(playlistPayload)}
          <ResponseBox state={responses['playlist-create']} />
        </section>

        <section className="panel">
          <div className="panel__title">
            <h2>Playlist</h2>
            <span className="pill pill--outline">add track</span>
          </div>
          <div className="form">
            <TextField
              label="Playlist ID"
              value={playlistTrack.playlistID}
              onChange={handleTextChange(setPlaylistTrack, 'playlistID')}
            />
            <TextField
              label="Track ID"
              value={playlistTrack.trackID}
              onChange={handleTextChange(setPlaylistTrack, 'trackID')}
            />
          </div>
          <button
            className="action"
            type="button"
            onClick={() =>
              authedPost(
                'playlist-add-track',
                'https://prod.apiollon.com/flow/nova-add-track-to-playlist',
                playlistTrack,
              )
            }
          >
            Add track to playlist
          </button>
          {renderJSON(playlistTrack)}
          <ResponseBox state={responses['playlist-add-track']} />
        </section>
      </div>
    </div>
  );
}

type TextFieldProps = {
  label: string;
  value: string | number;
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
  type?: string;
};

function TextField({ label, value, onChange, placeholder, type = 'text' }: TextFieldProps) {
  return (
    <label className="field">
      <span>{label}</span>
      <input type={type} value={value} onChange={onChange} placeholder={placeholder} />
    </label>
  );
}

type TextAreaProps = {
  label: string;
  value: string;
  onChange: (event: ChangeEvent<HTMLTextAreaElement>) => void;
};

function TextArea({ label, value, onChange }: TextAreaProps) {
  return (
    <label className="field">
      <span>{label}</span>
      <textarea value={value} onChange={onChange} rows={3} />
    </label>
  );
}

type ToggleProps = {
  label: string;
  checked: boolean;
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
};

function Toggle({ label, checked, onChange }: ToggleProps) {
  return (
    <label className="field toggle">
      <span>{label}</span>
      <div className="toggle__control">
        <input type="checkbox" checked={checked} onChange={onChange} />
        <div className="toggle__dot" />
      </div>
    </label>
  );
}

type ResponseBoxProps = {
  state?: ResponseState;
};

function ResponseBox({ state }: ResponseBoxProps) {
  if (!state || state.status === 'idle') return null;
  return (
    <div className={`response response--${state.status}`}>
      <strong>{state.status === 'success' ? 'OK' : state.status === 'loading' ? 'Loading' : 'Error'}</strong>
      <pre>{state.message}</pre>
    </div>
  );
}

export default App;
